import { useState, useEffect, useMemo, useCallback, startTransition } from 'react';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';

interface UseColumnOrderProps {
  columns: GridColDef[];
  storageKey: string; // Unique key for localStorage (e.g., 'quotations-columns', 'payments-columns')
  initialVisibility?: Record<string, boolean>; // Initial column visibility from columnVisibilityModel
}

interface ColumnOrderState {
  field: string;
  visible: boolean;
}

export const useColumnOrder = ({ columns, storageKey, initialVisibility }: UseColumnOrderProps) => {
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Separate actions column from regular columns
  const actionsColumn = useMemo(() => columns.find(col => col.field === 'actions'), [columns]);
  const regularColumns = useMemo(() => columns.filter(col => col.field !== 'actions'), [columns]);

  // Initialize column order from localStorage or default from columns prop
  useEffect(() => {
    if (isInitialized) {
      return;
    }

    const savedOrder = localStorage.getItem(storageKey);
    let nextOrder: ColumnOrderState[];

    if (savedOrder) {
      try {
        const parsed = JSON.parse(savedOrder) as ColumnOrderState[];
        const parsedWithoutActions = parsed.filter(item => item.field !== 'actions');

        const currentFields = regularColumns.map(col => col.field);
        const validSavedOrder = parsedWithoutActions.filter(item => currentFields.includes(item.field));

        const newFields = currentFields.filter(
          field => !validSavedOrder.find(item => item.field === field)
        );
        const newColumns = newFields.map(field => ({
          field,
          visible: initialVisibility?.[field] ?? true,
        }));

        nextOrder = [...validSavedOrder, ...newColumns];
      } catch (error) {
        console.error('Failed to parse saved column order:', error);
        nextOrder = regularColumns.map(col => ({
          field: col.field,
          visible: initialVisibility?.[col.field] ?? true,
        }));
      }
    } else {
      nextOrder = regularColumns.map(col => ({
        field: col.field,
        visible: initialVisibility?.[col.field] ?? true,
      }));
    }

    startTransition(() => {
      setColumnOrder(nextOrder);
      setIsInitialized(true);
    });
  }, [storageKey, regularColumns, initialVisibility, isInitialized]);

  // Save to localStorage whenever order changes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(columnOrder));
    }
  }, [columnOrder, storageKey]);

  // Reorder columns based on saved state
  const orderedColumns = useMemo(() => {
    if (columnOrder.length === 0) return columns;

    const columnMap = new Map(regularColumns.map(col => [col.field, col]));
    const ordered: GridColDef[] = [];

    // Apply order to regular columns
    columnOrder.forEach(({ field }) => {
      const column = columnMap.get(field);
      if (column) {
        ordered.push(column);
      }
    });

    // Always append actions column at the end if it exists
    if (actionsColumn) {
      ordered.push(actionsColumn);
    }

    return ordered;
  }, [columns, regularColumns, actionsColumn, columnOrder]);

  // Generate columnVisibilityModel from our state
  const columnVisibilityModel = useMemo(() => {
    const model: Record<string, boolean> = {};
    columnOrder.forEach(({ field, visible }) => {
      model[field] = visible;
    });
    // Always show actions column if it exists
    if (actionsColumn) {
      model['actions'] = initialVisibility?.['actions'] ?? true;
    }
    return model;
  }, [columnOrder, actionsColumn, initialVisibility]);

  // Move column up in order
  const moveColumnUp = useCallback((field: string) => {
    setColumnOrder(prev => {
      const index = prev.findIndex(col => col.field === field);
      if (index <= 0) return prev;

      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      return newOrder;
    });
  }, []);

  // Move column down in order
  const moveColumnDown = useCallback((field: string) => {
    setColumnOrder(prev => {
      const index = prev.findIndex(col => col.field === field);
      if (index === -1 || index >= prev.length - 1) return prev;

      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  }, []);

  // Toggle column visibility (ensure at least one non-actions column is visible)
  const toggleColumnVisibility = useCallback((field: string) => {
    setColumnOrder(prev => {
      // Count currently visible columns
      const visibleCount = prev.filter(col => col.visible).length;
      const targetColumn = prev.find(col => col.field === field);
      
      // Prevent hiding the last visible column
      if (targetColumn?.visible && visibleCount === 1) {
        return prev;
      }
      
      return prev.map(col =>
        col.field === field ? { ...col, visible: !col.visible } : col
      );
    });
  }, []);

  // Reset to default order
  const resetToDefault = useCallback(() => {
    const defaultOrder = regularColumns.map(col => ({
      field: col.field,
      visible: initialVisibility?.[col.field] ?? true,
    }));
    setColumnOrder(defaultOrder);
    localStorage.removeItem(storageKey);
  }, [regularColumns, initialVisibility, storageKey]);

  // Get column label from original columns
  const getColumnLabel = useCallback((field: string) => {
    const column = columns.find(col => col.field === field);
    return column?.headerName || field;
  }, [columns]);

  return {
    orderedColumns,
    columnOrder,
    columnVisibilityModel,
    moveColumnUp,
    moveColumnDown,
    toggleColumnVisibility,
    resetToDefault,
    getColumnLabel,
  };
};
